import { cartAtom } from "@/atoms"
import { Button } from "@/components/ui"
import { freeItemInBag, type PrizeChoice, type PrizeOffer } from "@/domain/marketing/prize-offer"
import { useAddFreeItem } from "@/hooks/use-add-free-item"
import { usePrizeOffer } from "@/hooks/use-redeem-prize"
import type { CouponTerms } from "@/lib/coupon-readiness"
import { useAtomValue } from "jotai"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { PrizeGameButton } from "../PrizeGameButton"
import { PrizeChoiceList } from "../prize-redeem/PrizeChoiceList"

type CouponFreeItemActionProps = {
  terms: CouponTerms | null | undefined
  tone: "game" | "store"
  onDone?: (added: boolean) => void
}

type Plan =
  | { kind: "add-one"; choice: PrizeChoice; label: string }
  | { kind: "add-all"; choices: PrizeChoice[]; label: string }
  | { kind: "pick"; choices: PrizeChoice[]; label: string }

function planOf(terms: CouponTerms, offer: PrizeOffer): Plan | null {
  if (terms.offer?.kind !== "free-items") return null
  if (offer.kind === "add") {
    return { kind: "add-one", choice: offer.choice, label: offer.choice.needsOptions ? "Escolher e adicionar à sacola" : "Adicionar à sacola" }
  }
  if (offer.kind !== "pick") return null
  const wantsAll = terms.offer.pick === "each" && offer.choices.length === terms.offer.refs.length && offer.choices.every((choice) => !choice.needsOptions)
  return wantsAll
    ? { kind: "add-all", choices: offer.choices, label: "Adicionar à sacola" }
    : { kind: "pick", choices: offer.choices, label: "Escolher e adicionar à sacola" }
}

/**
 * The "Adicionar à sacola" action of a free-items coupon: puts the coupon's
 * product (or the picked one, or every one of a "ganha todos" coupon) in the
 * bag. Hidden for any other coupon and once one of its products is already in
 * the bag; a product with required options opens its page instead. `onDone` learns whether everything landed in the bag.
 */
export function CouponFreeItemAction({ terms, tone, onDone }: CouponFreeItemActionProps) {
  const navigate = useNavigate()
  const cart = useAtomValue(cartAtom)
  const addFreeItem = useAddFreeItem()
  const offer = usePrizeOffer(terms?.offer?.kind === "free-items" ? terms.benefit : null)
  const [picking, setPicking] = useState(false)
  if (!terms || !offer || freeItemInBag(offer, cart.items)) return null
  const plan = planOf(terms, offer)
  if (!plan || terms.offer?.kind !== "free-items") return null
  const quantity = terms.offer.quantity

  function take(choices: readonly PrizeChoice[]) {
    let added = true
    for (const choice of choices) {
      const addition = addFreeItem(choice, quantity)
      if (!addition.added) {
        added = false
        navigate(addition.to)
      }
    }
    onDone?.(added)
  }

  if (plan.kind === "pick" && picking) return <PrizeChoiceList tone={tone} choices={plan.choices} onChoose={(choice) => take([choice])} />

  function act() {
    if (!plan) return
    if (plan.kind === "pick") setPicking(true)
    else take(plan.kind === "add-one" ? [plan.choice] : plan.choices)
  }

  return tone === "game" ? (
    <PrizeGameButton onClick={act}>{plan.label}</PrizeGameButton>
  ) : (
    <Button size="sm" variant="soft" onClick={act} className="self-start">
      {plan.label}
    </Button>
  )
}
